import { useRef } from 'react';
import type { PoolOfferDetail } from '../../../modules/api';
import { canAffordOffer, useWallet } from '../../../modules/wallet';
import { openDepositUrl, useOperatorLinks } from '../../../modules/operatorLinks';
import { usePayments } from '../../../modules/payments/usePayments';

export interface DepositToRip {
  /** The cached balance doesn't cover the picked packs and the player has
   *  somewhere to top up — the buy button reads "Deposit to rip". */
  isShortOfFunds: boolean;
  /** The buy button's tap: a purchase, or straight to the cashier. */
  onBuy: () => void;
}

/**
 * Sleek's "Deposit to rip" (partner ask, Betr 2026-10-23): a player who
 * can't afford the packs goes straight to the deposit page instead of
 * through the "Insufficient balance" dialog.
 *
 * Stripe operators keep `buy()`: it already opens the in-iframe deposit
 * screen and rips once funded. Seamless operators go to `deposit_url`
 * (the host gets `navigate_deposit` first); without one nothing changes.
 */
export function useDepositToRip(
  offer: PoolOfferDetail | null,
  quantity: number,
  buy: () => void,
): DepositToRip {
  const wallet = useWallet();
  const { depositUrl } = useOperatorLinks();
  const { isStripeFunded } = usePayments();
  // Covers the balance re-read, which neither `buy()` nor the cashier guard sees.
  const isCheckingRef = useRef(false);

  const canTopUp = isStripeFunded || depositUrl !== null;
  const isShortOfFunds =
    canTopUp && offer !== null && !canAffordOffer(offer.price, quantity, wallet.balance_cents);

  function onBuy() {
    if (!offer || !isShortOfFunds || isStripeFunded || !depositUrl) {
      buy();
      return;
    }
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    // The cached balance goes stale after a deposit the host doesn't report,
    // so the label may be wrong: a fresh read decides between rip and cashier.
    void wallet
      .refresh()
      .then((fresh) => {
        if (canAffordOffer(offer.price, quantity, fresh)) buy();
        else openDepositUrl(depositUrl);
      })
      .finally(() => {
        isCheckingRef.current = false;
      });
  }

  return { isShortOfFunds, onBuy };
}
